import React, { memo } from 'react';
import { ReturnsTableRow } from '../../types/performance';
import { fmtPct } from '../../utils/performanceFormat';

interface ReturnsComparisonTableProps {
  rows: ReturnsTableRow[];
  selectedPeriod: string;
  onPeriodSelect: (period: string) => void;
  loading: boolean;
}

/**
 * Portfolio vs S&P 500 (total return) per period, as stored in
 * gold.portfolio_period_kpis: time-weighted, dividends included, the same
 * sessions for both; annualised only from a year (GIPS).
 */
const ReturnsComparisonTable: React.FC<ReturnsComparisonTableProps> = ({
  rows,
  selectedPeriod,
  onPeriodSelect,
  loading
}) => {
  if (loading) {
    return <div className="perf-rt-state">Loading returns...</div>;
  }
  if (!rows || rows.length === 0) {
    return null;
  }

  const cell = (value: number | null) => (
    <span className={`n ${value == null ? 'is-dim' : value >= 0 ? 'is-gain' : 'is-loss'}`}>{fmtPct(value)}</span>
  );

  return (
    <div className="perf-rt-wrap">
      <h3 className="perf-rt-h">Returns Comparison</h3>
      <table className="perf-rt">
        <thead>
          <tr>
            <th>Period</th>
            <th className="is-you">Portfolio</th>
            <th>S&amp;P 500</th>
            <th>Alpha</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const isSelected = selectedPeriod === row.period;
            return (
              <tr
                key={row.period}
                onClick={row.selectable ? () => onPeriodSelect(row.period) : undefined}
                className={`${row.selectable ? 'is-click' : ''} ${isSelected ? 'is-sel' : ''}`}
                aria-selected={isSelected}
              >
                <td className="perf-rt-p">
                  {row.period}
                  {row.annualised && <small title="A year or longer: shown per year (GIPS)"> /yr</small>}
                </td>
                <td className="num">{cell(row.portfolio)}</td>
                <td className="num">{cell(row.benchmark)}</td>
                <td className="num">{cell(row.alpha)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default memo(ReturnsComparisonTable);
